// NPCの具体的な行動実行を担当するモジュール
import { getCardName } from '../utils/cardUtils.js';

export const NPCActions = {
    // プレイ可能なカードを取得
    getPlayableCards: (player, gameState) => {
        if (!player || !gameState) return [];

        // 規模条件を満たすカードを取得
        const effectiveScale = player.scale; // 簡易版：getEffectiveScaleは後で統合

        // 「原始共産制」が有効かどうかをチェック (ID: 22)
        const isPrimitiveCommunismActive = player.ideology && player.ideology.id === 22;

        return player.hand.filter(card => {
            // 規模条件チェック
            if (card.required_scale > effectiveScale) return false;

            // 「原始共産制」が有効な場合、財カード(1)はプレイ不可
            if (isPrimitiveCommunismActive && card.card_type === 1) {
                return false;
            }

            // --- 特殊カードの戦略的プレイ判定 ---
            // 秩序の瓦解 (ID: 14) の判定
            if (card.id === 14) {
                if (!NPCActions.shouldPlayCollapseOfOrder(player, gameState)) {
                    return false;
                }
            }

            // 財カードの場合は場の上限もチェック
            if (card.card_type === 1) {
                return player.field.length < player.field_limit;
            }

            return true;
        });
    },

    // 秩序の瓦解をプレイすべきか判定する
    shouldPlayCollapseOfOrder: (player, gameState) => {
        const opponentId = player.id === 'player1' ? 'player2' : 'player1';
        const opponent = gameState.players[opponentId];
        // 相手が存在し、イデオロギーを持っている場合のみプレイする
        return opponent && !!opponent.ideology;
    },

    // 選択肢から戦略的に選択
    makeStrategicChoice: (options, choiceType, awaitingInput, gameState) => {
        if (!options || options.length === 0) return null;

        // 秩序の瓦解の効果解決時 (CHOICE_CARD_FOR_EFFECT で、ソースが ID 14)
        if (choiceType === 'card_for_effect' && awaitingInput?.source_card_id === 14) {
            return NPCActions.decideCollapseOfOrderTarget(options, gameState);
        }

        // 基本はランダム（将来的に他の戦略を追加可能）
        return NPCActions.makeRandomChoice(options, choiceType);
    },

    // 秩序の瓦解のターゲット選択ロジック
    decideCollapseOfOrderTarget: (options, gameState) => {
        const npcPlayerId = gameState.current_turn;
        const opponentId = npcPlayerId === 'player1' ? 'player2' : 'player1';

        // 相手のイデオロギーが選択肢にあれば優先する
        const opponentIdeology = options.find(opt => opt.owner_id === opponentId);
        if (opponentIdeology) {
            console.log('[NPCActions] Collapse of Order: Choosing opponent ideology');
            return opponentIdeology;
        }

        // なければ自分のもの（基本 shouldPlay で弾いているがフォールバック）
        console.log('[NPCActions] Collapse of Order: No opponent ideology found, falling back');
        return options[0];
    },

    // カードプレイの実行
    executeCardPlay: (player, gameState, onPlayCard) => {
        const playableCards = NPCActions.getPlayableCards(player, gameState);

        if (playableCards.length > 0) {
            let cardToPlay;
            if (gameState?.isHypothesisMode) {
                // 仮説検証モード: 必ず手札の先頭からプレイ可能なカードを使用する
                cardToPlay = player.hand.find(card => 
                    playableCards.some(pc => pc.instance_id === card.instance_id)
                );
            } else {
                // 通常AI: ランダムにカードを選択してプレイ
                cardToPlay = playableCards[Math.floor(Math.random() * playableCards.length)];
            }

            if (cardToPlay) {
                console.log('[NPCActions] Playing card:', getCardName(cardToPlay.id));
                onPlayCard(cardToPlay);
                return true;
            }
        }

        return false;
    },

    // ターン終了の実行
    executeEndTurn: (onEndTurn) => {
        console.log('[NPCActions] Ending turn');
        onEndTurn();
    },

    // ターン継続判定（Python版準拠）
    shouldContinueTurn: (cardsPlayedThisTurn = 0) => {
        let endTurnProbability = 0.3; // 基本30%
        endTurnProbability += cardsPlayedThisTurn * 0.2; // カード1枚毎に20%増加

        const shouldEnd = Math.random() < endTurnProbability;
        console.log('[NPCActions] Turn continuation check:', {
            cardsPlayed: cardsPlayedThisTurn,
            endProbability: endTurnProbability,
            shouldEnd
        });

        return !shouldEnd;
    },

    // 選択肢からランダム選択
    makeRandomChoice: (options, choiceType) => {
        if (!options || options.length === 0) {
            console.warn('[NPCActions] No options available for choice:', choiceType);
            return null;
        }

        const randomChoice = options[Math.floor(Math.random() * options.length)];
        console.log('[NPCActions] Random choice made:', {
            type: choiceType,
            choice: randomChoice.id ? getCardName(randomChoice.id) : randomChoice,
            totalOptions: options.length
        });

        return randomChoice;
    },

    // 数値範囲からランダム選択
    makeRandomNumber: (min = 0, max = 1) => {
        const randomNumber = Math.floor(Math.random() * (max - min + 1)) + min;
        console.log('[NPCActions] Random number chosen:', randomNumber, `(range: ${min}-${max})`);
        return randomNumber;
    },

    // 複数カードのランダム選択
    makeRandomCardSelection: (options, count) => {
        if (!options || options.length === 0) return [];

        const selectedCount = Math.min(count || 1, options.length);
        const shuffled = [...options].sort(() => Math.random() - 0.5);
        const selectedCards = shuffled.slice(0, selectedCount);

        console.log('[NPCActions] Multiple cards selected:', {
            selected: selectedCards.map(c => getCardName(c.id)),
            count: selectedCount,
            totalOptions: options.length
        });

        return selectedCards;
    },

    // マリガンするカードの選択ロジック
    decideMulligan: (player, gameState) => {
        const cardsToMulligan = [];
        // 現在の規模より2大きいカードをマリガン対象とする（仮のロジック）
        // 例えば、規模5で手札に規模8のカードがあったらマリガン
        const mulliganThreshold = player.scale + 2; 

        player.hand.forEach(card => {
            if (card.required_scale > mulliganThreshold) {
                cardsToMulligan.push(card);
            }
        });

        console.log(`[NPCActions] NPC deciding mulligan. Player scale: ${player.scale}, Threshold: ${mulliganThreshold}. Cards to mulligan:`, cardsToMulligan.map(c => getCardName(c.id)));
        return cardsToMulligan;
    },

    // 再開発計画を使用すべきか判定する
    shouldUseRedevelopmentPlan: (player, gameState) => {
        if (!player || !gameState) return false;

        // 1ターン1度の制限チェック
        if (player.redevelopment_used) return false;

        // 場の財カード数が場の上限の半数より多いか確認
        const fieldWealthCount = player.field.filter(c => c.card_type === 1).length;
        if (fieldWealthCount <= Math.floor(player.field_limit / 2)) return false;

        // 「場の財カード数が場の上限に達している」条件
        const isFieldFull = player.field.length >= player.field_limit;

        // 「手札に現在の実効規模以下の必要規模を持つカードがない」条件
        // マネー(id:33)の耐久値を規模に加算した実効規模を計算
        const moneyCard = player.field.find(c => c.id === 33);
        const effectiveScale = player.scale + (moneyCard ? (moneyCard.current_durability || 0) : 0);
        const hasPlayableCard = player.hand.some(card => card.required_scale <= effectiveScale);

        return isFieldFull && !hasPlayableCard;
    },

    // 再開発計画で除外する財カードを選択する（最も耐久値の低いカード）
    selectRedevelopmentTarget: (player) => {
        if (!player || player.field.length === 0) return null;
        const wealthCards = player.field.filter(c => c.card_type === 1);
        if (wealthCards.length === 0) return null;

        return wealthCards.reduce((lowest, card) => {
            const durability = card.current_durability !== undefined ? card.current_durability : card.durability;
            const lowestDurability = lowest.current_durability !== undefined ? lowest.current_durability : lowest.durability;
            return durability < lowestDurability ? card : lowest;
        });
    },

    // チュートリアル用のスクリプト実行
    executeScriptedTurn: (player, gameState, script, onPlayCard, onEndTurn) => {
        const playedThisTurn = player.cards_played_this_turn || 0;
        const targetCount = script.count || 1;

        console.log(`[NPCActions] Scripted Turn: played=${playedThisTurn}, target=${targetCount}`);

        if (script.command === 'PLAY_OLD_CARDS') {
            // 指定枚数に達していない場合のみプレイを試行
            if (playedThisTurn < targetCount) {
                // 古い順にプレイ可能なものを探す
                for (let i = 0; i < player.hand.length; i++) {
                    const card = player.hand[i];
                    if (card.required_scale <= player.scale) {
                        console.log(`[NPCActions] Scripted Play (${playedThisTurn + 1}/${targetCount}): ${getCardName(card.id)}`);
                        onPlayCard(card);
                        return true; 
                    }
                }
                // プレイ可能なものがない場合は、あきらめて次（ターン終了）へ
                console.log('[NPCActions] No playable cards for script.');
            }

            // 指定枚数終了、またはプレイ不能な場合
            if (script.autoEndTurn) {
                console.log('[NPCActions] Scripted Turn End triggered.');
                onEndTurn();
                return true;
            }
        }
        return false;
    }
};

export default NPCActions;